import CampaignLayout from 'Layouts/CampaignLayout'
import React, { useCallback, useState } from 'react'
import { Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import DataGrid, { Column, TextEditor } from 'react-data-grid'
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { attachIGPermalink } from 'services/api/campaign'
import { AppDispatch, RootState } from 'store'
import Swal from 'sweetalert2'
import { campaignIconSVG } from '../Campaign'
import CampaignLinksStatus from '../LinksStatus'
import { formatIGPostLink, getErrorMessage, scrollToElement, trimQueryParamsFromURL, uniqueByKey } from 'utils/helpers'
import { fetchCampaignPermalinksThunk } from 'slices/campaigns/permalinks/thunk'
import withAccessControl from 'hoc/withAccessControl'
import { PRIVILEGE } from 'constants/privilege'
import useDocumentTitle from 'hooks/useDocumentTitle'

interface Row {
    [key: string]: string;
}

interface ColumnDef {
    label: string;
    slug: string;
    required?: boolean;
    regex?: string;
    width?: number;
}

interface RowError {
    rowIdx: number,
    colId: string,
    error: string
}

const defaultColumns: ColumnDef[] = [
    { slug: "permalink", label: "Post Link", regex: "^https:\/\/www\.instagram\.com\/.+$", width: 75 },
    { slug: "earning", label: "Amount (Paid to creator)", width: 25 },
];

const validationRegex = "^https:\/\/www\.instagram\.com\/.+$";

const CampaignPermalinks = () => {
    const { campaignId } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const selectedAgencyId = useSelector(
        (state: RootState) => state.auth.selectedAgencyId
    );

    const navigate = useNavigate();

    const [permalink, setPermalink] = useState("");
    const [rows, setRows] = useState<Row[]>([]);
    const [columns, setColumns] = useState<ColumnDef[]>(defaultColumns);
    const [rowsErrors, setRowsErrors] = useState<RowError[]>([]);
    const [processing, setProcessing] = useState(false);
    const [overlay, setOverlay] = useState<{ visible: boolean, text?: string }>({ visible: false });

    // For mapping modal
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [uploadedHeaders, setUploadedHeaders] = useState<string[]>([]);
    const [tempRows, setTempRows] = useState<Row[]>([]);
    const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});

    const appliedListFilters = useSelector((state: RootState) => state.campaignPermalinks.filters);

    useDocumentTitle("Track Posts - Campaign");

    const hasError = (rowIdx: number, colId: string) => {
        return rowsErrors.some(err => err.rowIdx === rowIdx && err.colId === colId);
    };

    const dataGridColumns: Column<Row>[] =
        columns.length > 0
            ? columns.map((col) => {
                return {
                    key: col.slug,
                    name: col.label,
                    editor: TextEditor,
                    width: (col.width ?? 25) + "%",
                    renderCell: ({ row }: { row: Row }) => (
                        <div className={col.slug === 'earning' ? 'text-end' : ''}>
                            {row[col.slug]}
                        </div>
                    ),
                    cellClass: (row: Row) =>
                        hasError(rows.indexOf(row), col.slug) ? 'error-cell' : "",
                }
            })
            : [];

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        setOverlay({ visible: true, text: 'Importing...' });

        const reader = new FileReader();
        const fileExt = file.name.split('.').pop()?.toLowerCase();

        if (fileExt === "csv") {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (results: any) => {
                    const uploadedRows = results.data as Row[];
                    const headers = Object.keys(uploadedRows[0] || {});
                    setUploadedHeaders(headers);
                    setTempRows(uploadedRows);
                    setOverlay({ visible: false });
                    setShowMappingModal(true);
                }
            });
        } else if (fileExt === "xlsx" || fileExt === "xls") {
            reader.onload = (e) => {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: Row[] = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
                const headers = Object.keys(jsonData[0] || {});
                setUploadedHeaders(headers);
                setTempRows(jsonData);
                setOverlay({ visible: false });
                setShowMappingModal(true);
            };
            reader.readAsArrayBuffer(file);
        } else {
            alert("Unsupported file type. Please upload a CSV or Excel file.");
            setOverlay({ visible: false });
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "text/csv": [".csv"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.ms-excel": [".xls"]
        }
    });

    const addLink = () => {
        const regex = new RegExp(validationRegex);
        if (!regex.test(permalink)) {
            return toast.error("Please enter a valid Instagram post link");
        }
        setRows(uniqueByKey([...rows, { permalink: formatIGPostLink(trimQueryParamsFromURL(permalink)), earning: '0' }], 'permalink'));
        setPermalink("");
    };

    // Apply mapping
    const applyMapping = () => {
        const mappedRows: Row[] = tempRows.map(r => {
            const newRow: Row = {};
            columns.forEach(col => {
                const sourceHeader = columnMapping[col.slug];
                const value = col.slug === 'permalink' ? formatIGPostLink(trimQueryParamsFromURL(r[sourceHeader] ?? '')) : r[sourceHeader];
                newRow[col.slug] = value || "";
            });
            return newRow;
        });

        setRows(uniqueByKey([...rows, ...mappedRows], 'permalink'));
        setShowMappingModal(false);
        setColumnMapping({});
    };

    // Validation function
    const validateRows = () => {
        const errors: RowError[] = [];

        rows.forEach((row, rowIdx) => {
            columns.forEach((col) => {
                const value = typeof(row[col.slug]) === 'string' ? row[col.slug]?.trim() : row[col.slug] || "";

                if (col.required && !value) {
                    errors.push({
                        rowIdx,
                        colId: col.slug,
                        error: `${col.label} is required`,
                    });
                }

                if (col.regex && value) {
                    const regex = new RegExp(col.regex);
                    if (!regex.test(value)) {
                        errors.push({
                            rowIdx,
                            colId: col.slug,
                            error: `${col.label} is invalid`,
                        });
                    }
                }
            });
        });

        setRowsErrors(errors);
        return errors.length === 0;
    };

    const saveRows = async () => {
        if (rows.length === 0 || !campaignId || !selectedAgencyId || processing) return;

        const isValid = validateRows();
        if (!isValid) {
            Swal.fire({
                title: "Validation Error",
                text: "Some rows contain errors. Please fix them before saving.",
                icon: "error"
            });
            return;
        }

        const result = await Swal.fire({
            title: `Add ${rows.length} ${rows.length > 1 ? 'posts' : 'post'} to this campaign?`,
            iconHtml: campaignIconSVG,
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Yes, Add",
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        setProcessing(true);

        try {
            const response = await attachIGPermalink(campaignId, selectedAgencyId, {posts: rows.map((row) => ({...row, earning: Number(row.earning ?? 0)}))})
            if(response){
                scrollToElement('linked-posts')
                dispatch(fetchCampaignPermalinksThunk({ campaignId, force: true, filters: { ...appliedListFilters, page: 1 } }))
                setRows([]);
            }            
        } catch (error: any) {
            toast.error(getErrorMessage(error, "Failed to link posts!"));
        } finally {
            setProcessing(false);
        }
    };

    return (
        <CampaignLayout>
            <div className="card campaign-insights-card">
                <div className="card-header d-flex justify-content-between align-items p-4 border-0 flex-column flex-lg-row gap-2 gap-lg-0">
                    <h1>Add Posts</h1>
                </div>
                <div className="divider"></div>
                <div className="card-body row g-4">
                    <div className="d-flex flex-column col-lg-6 gap-2">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                            <Input
                                type='text'
                                value={permalink}
                                onChange={(e) => setPermalink(e.target.value)}
                                placeholder='Paste post link (URL) from Instagram'
                            />
                            <button onClick={addLink} disabled={!permalink} className='btn btn-soft-primary add-link-btn'>Add Link</button>
                        </div>
                        <div className="sheet-upload d-flex flex-column gap-2">
                            <span className='sheet-upload-label'>You can also upload a CSV/XLSX file containing post links</span>
                            <div
                                {...getRootProps()}
                                className={`dropzone p-4 text-center d-flex align-items-center justify-content-between ${isDragActive ? "active" : ""}`}
                                style={{ minHeight: '274px', border: "2px dotted #ccc" }}
                            >
                                <input {...getInputProps()} />
                                <div className="d-flex align-items-center flex-column justify-content-center gap-3 w-100 h-100">
                                    <button className='btn btn-soft-primary'>
                                        <i className='ri-upload-cloud-line'></i> Upload File
                                    </button>
                                    <p className="mb-0">
                                        {isDragActive ? "Drop the file here ..." : "or simply drag & drop CSV/XLSX file here"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div className="rdg-light track-posts-sheet" style={{ height: "calc(85vh - 450px)", display: "flex", flexDirection: "column" }}>
                            <div style={{ flex: 1 }}>
                                <DataGrid
                                    className="rdg-light bulk-process-sheet h-100"
                                    columns={dataGridColumns}
                                    rows={rows}
                                    onRowsChange={setRows}
                                    headerRowHeight={50}
                                    rowKeyGetter={(row: Row) => row.permalink}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-footer d-flex justify-content-end">
                    <button onClick={saveRows} disabled={!rows.length} className='btn btn-gradient'>Add posts to campaign</button>
                </div>
            </div>
            <div id="linked-posts">
                <CampaignLinksStatus />
            </div>

            {/* Mapping Modal */}
            <Modal isOpen={showMappingModal} toggle={() => setShowMappingModal(false)} centered>
                <ModalHeader toggle={() => setShowMappingModal(false)}>
                    Map Columns
                </ModalHeader>
                <ModalBody>
                    <p className="mb-3">Map your sheet columns to required fields:</p>
                    {columns.map(col => (
                        <div key={col.slug} className="mb-3">
                            <label className="form-label">{col.label}</label>
                            <select
                                className="form-select"
                                value={columnMapping[col.slug] || ""}
                                onChange={e => setColumnMapping({ ...columnMapping, [col.slug]: e.target.value })}
                            >
                                <option value="">-- Select column --</option>
                                {uploadedHeaders.map(h => (
                                    <option key={h} value={h}>{h}</option>
                                ))}
                            </select>
                        </div>
                    ))}
                </ModalBody>
                <ModalFooter>
                    <button
                        className="btn btn-gradient-outline"
                        onClick={() => setShowMappingModal(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-gradient"
                        disabled={columns.some(col => !columnMapping[col.slug])}
                        onClick={applyMapping}
                    >
                        Apply
                    </button>
                </ModalFooter>
            </Modal>
        </CampaignLayout>
    )
}

export default withAccessControl(CampaignPermalinks, PRIVILEGE.CAMPAIGN.COLLABORATION.PROCESS)
